import { Flex } from "@otrium/core";
import {
  RangeBoundaries,
  RangeMax,
  RangeMin,
} from "instantsearch.js/es/connectors/range/connectRange";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCurrentRefinements, useRange } from "react-instantsearch-core-v7";
import { DropdownOutlined } from "src/atoms/DropDown";
import NoSSR from "src/atoms/NoSSR";
import { useOnClickOutside } from "src/hooks/useOnClickOutside";
import { debounce } from "src/utils";
import { PriceInputs, PriceRange } from "../Range";
import { ResultsButton } from "src/organisms/AlgoliaProductList/DesktopFilterVeiw/DesktopFilter.styled";
import { Trans } from "@lingui/macro";
import { useSegment } from "src/hooks/useSegment";
import { useHandleSearchParams } from "src/hooks/use-handle-search-params";

function Range({
  attribute,
  placeholder,
  totalResults: total,
  handleScrollToTop,
}: {
  attribute: string;
  placeholder: string;
  totalResults: number;
  handleScrollToTop: () => void;
}): JSX.Element {
  const { items } = useCurrentRefinements();
  const { refine, range, canRefine } = useRange({ attribute });
  const { items: currentRefinements } = useCurrentRefinements({
    includedAttributes: [attribute],
  });
  const filterRef = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const { segmentProductListFilterOpened } = useSegment();

  const toggleOpen = debounce(() => {
    setOpen(!isOpen);

    if (!isOpen) {
      void segmentProductListFilterOpened(attribute);
    }
  }, 100);

  useOnClickOutside(filterRef, toggleOpen, isOpen);

  const [rangeValue, setRangeValue] = useState<RangeBoundaries>([0, 0]);
  const { minValue, maxValue } = useMemo(() => {
    const priceFilter = items.find((item) => item.attribute === attribute);
    const minValue =
      (priceFilter?.refinements.find((item) => item.operator === ">=")
        ?.value as RangeMin) || range.min;
    const maxValue =
      (priceFilter?.refinements.find((item) => item.operator === "<=")
        ?.value as RangeMax) || range.max;

    setRangeValue([Number(minValue), Number(maxValue)]);
    return { minValue, maxValue };
  }, [items, range.min, range.max, attribute]);

  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (
      !isFirstRenderRef.current ||
      !Number.isFinite(rangeValue[0]) ||
      !Number.isFinite(rangeValue[1]) ||
      !canRefine
    ) {
      return;
    }

    const url = new URL(location.toString());
    const minPrice = Number(url.searchParams.get("min_price") || rangeValue[0]);
    const maxPrice = Number(url.searchParams.get("max_price") || rangeValue[1]);

    refine([minPrice, maxPrice]);

    isFirstRenderRef.current = false;
  }, [refine, rangeValue, canRefine]);

  const { handleSearchParamsPrice } = useHandleSearchParams();

  const handleRefine = useCallback(() => {
    handleSearchParamsPrice({
      min: rangeValue[0] !== minValue ? rangeValue[0] : undefined,
      max: rangeValue[1] !== maxValue ? rangeValue[1] : undefined,
    });

    refine(rangeValue);

    handleScrollToTop();
  }, [
    rangeValue,
    minValue,
    maxValue,
    refine,
    handleSearchParamsPrice,
    handleScrollToTop,
  ]);

  const handleChange = useCallback(
    ([rangeMin, rangeMax]: number[]) => setRangeValue([rangeMin, rangeMax]),
    []
  );

  return (
    <DropdownOutlined opened={isOpen}>
      <DropdownOutlined.Button onClick={toggleOpen}>
        <span>{placeholder}</span>
      </DropdownOutlined.Button>
      {isOpen ? (
        <DropdownOutlined.Items>
          <Flex
            ref={filterRef}
            paddingBlockStart={3}
            paddingBlockEnd={3}
            flexDirection="column"
            maxHeight="inherit"
          >
            <NoSSR>
              <Flex mb={2} alignItems="center">
                <PriceInputs
                  range={[String(rangeValue[0]), String(rangeValue[1])]}
                  onInputChange={(index) => (e) => {
                    const newRangeValue: RangeBoundaries = [...rangeValue];
                    newRangeValue[index] = Number(e.currentTarget.value);

                    handleChange(newRangeValue as number[]);
                  }}
                  onBlurHandler={() => handleRefine}
                />
              </Flex>
            </NoSSR>
            <PriceRange
              range={[String(rangeValue[0]), String(rangeValue[1])]}
              onSliderChange={handleChange}
              handleRange={handleRefine}
              initialMinPrice={Number(range.min)}
              initialMaxPrice={Number(range.max)}
            />

            <Flex variant="centered">
              <ResultsButton
                fullWidth
                variant="primary"
                onClick={toggleOpen}
                disabled={currentRefinements.length === 0 || total === 0}
              >
                <span>
                  {currentRefinements.length > 0 && total > 0 ? (
                    <Trans>Show {total} results</Trans>
                  ) : (
                    <Trans>Show results</Trans>
                  )}
                </span>
              </ResultsButton>
            </Flex>
          </Flex>
        </DropdownOutlined.Items>
      ) : null}
    </DropdownOutlined>
  );
}

export { Range };
